import { styled } from '@linaria/react'
import { MinimalSkeleton } from '@ovasabi/ui-minimal'
import { from, minimalVars } from '@ovasabi/ui-minimal/tokens'
import { skeletonShimmer, skeletonShimmerDark } from '../../styles/skeleton'

export const Skeleton = styled.div<{
  $width?: string
  $height?: string
  $radius?: string
  $dark?: boolean
}>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '1rem'};
  &:not([data-dark]) {
    ${skeletonShimmer}
  }
  &[data-dark] {
    ${skeletonShimmerDark}
  }
  border-radius: ${({ $radius }) => $radius || '14px'};
  display: block;
`

const FeatureSkeletonCard = styled.div`
  position: relative;
  overflow: hidden;
  background: ${minimalVars.color.textPrimary};
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 18px 20px;
  display: grid;
  gap: 10px;
  min-height: 154px;
  box-sizing: border-box;

/* 520px is between the hand and page stops, so it stays hand-typed. */
    @media (max-width: 520px) {
      padding: 14px 16px;
      gap: 6px;
      min-height: 140px;
    }
`

const FeatureShowcaseSkeleton = styled.div`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 76px;
  height: 76px;
  border-radius: 14px;
  ${skeletonShimmerDark}
  border: 1px solid rgba(255, 255, 255, 0.2);

  /* 520px is between the hand and page stops, so it stays hand-typed. */
  @media (max-width: 520px) {
    width: 58px;
    height: 58px;
    right: 12px;
  }
`

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const FeatureCardSkeleton = () => (
  <FeatureSkeletonCard aria-busy="true" aria-label="Loading feature">
    <FeatureShowcaseSkeleton />
    <SkeletonRow>
      <Skeleton data-dark $width="14px" $height="14px" $radius="3px" />
      <Skeleton data-dark $width="110px" $height="11px" $radius="4px" />
    </SkeletonRow>
    <Skeleton data-dark $width="60%" $height="22px" $radius="6px" />
    <Skeleton data-dark $width="70%" $height="13px" $radius="4px" />
    <SkeletonRow style={{ marginTop: 6 }}>
      <Skeleton data-dark $width="96px" $height="32px" $radius="6px" />
      <Skeleton data-dark $width="70px" $height="14px" $radius="4px" />
    </SkeletonRow>
  </FeatureSkeletonCard>
)

const RailTileContainer = styled.div`
  flex-shrink: 0;
  width: clamp(145px, 38vw, 175px);
  display: flex;
  flex-direction: column;
  border: 1px solid ${minimalVars.color.borderSubtle};
  background: ${minimalVars.color.bgSurface};
  padding: 0;
  overflow: hidden;
  border-radius: ${minimalVars.radius.md};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.02);

  ${from('page')} {
    width: clamp(180px, 18vw, 210px);
  }
`

const TileMediaSkeleton = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  ${skeletonShimmer}
`

const TileFootSkeleton = styled.div`
  display: grid;
  gap: 6px;
  padding: 10px 12px 14px;
  width: 100%;
  box-sizing: border-box;
`

export const RailTileSkeleton = () => (
  <RailTileContainer aria-busy="true" aria-label="Loading item">
    <TileMediaSkeleton />
    <TileFootSkeleton>
      <Skeleton $width="85%" $height="14px" $radius="4px" />
      <Skeleton $width="50%" $height="11px" $radius="3px" />
    </TileFootSkeleton>
  </RailTileContainer>
)

const SlotCard = styled.div`
  background: ${minimalVars.color.bgSurface};
  border: 1px solid ${minimalVars.color.borderSubtle};
  border-radius: 18px;
  padding: 12px;
  display: grid;
  gap: 8px;
`

export const MealSlotSkeleton = () => (
  <SlotCard aria-busy="true" aria-label="Loading meal slot">
    <SkeletonRow style={{ justifyContent: 'space-between' }}>
      <SkeletonRow>
        <Skeleton $width="18px" $height="18px" $radius="4px" />
        <Skeleton $width="80px" $height="14px" $radius="4px" />
      </SkeletonRow>
      <Skeleton $width="60px" $height="12px" $radius="4px" />
    </SkeletonRow>
    <Skeleton $width="100%" $height="44px" $radius="6px" />
  </SlotCard>
)

const ListSkeletonFrame = styled.div`
  display: grid;
  gap: ${minimalVars.space.xs};
  width: 100%;
`

const ListSkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  gap: ${minimalVars.space.xs};
  padding: ${minimalVars.space.xs};
  border-radius: ${minimalVars.radius.md};
  background: ${minimalVars.color.bgSurface};
  > div { display: grid; gap: 8px; min-width: 0; }
`

/**
 * Placeholder rows for a list whose data has not arrived yet.
 *
 * Shown instead of an empty state while the first projection snapshot is in
 * flight. Without it, every list page opened on "No orders yet" or "No
 * conversations" and then filled in a second later, which reads as the data
 * having been lost.
 */
export const ListSkeleton = ({ rows = 3, label = 'Loading' }: { rows?: number; label?: string }) => (
  <ListSkeletonFrame role="status" aria-busy="true" aria-label={label}>
    {Array.from({ length: rows }, (_, index) => (
      <ListSkeletonRow key={index}>
        <MinimalSkeleton width="56px" height="56px" radius={minimalVars.radius.md} />
        <div>
          <MinimalSkeleton width={index % 2 ? '52%' : '68%'} height="14px" />
          <MinimalSkeleton width="38%" height="12px" />
        </div>
      </ListSkeletonRow>
    ))}
  </ListSkeletonFrame>
)
